/**
 * Motives that mean a lesson does not take place.
 *
 * Mirrors `SubstitutionMotive.NotOccurringMotives` of the backend, except for `ShiftedFrom`:
 * a moved lesson is already communicated as a move ("Verlegt von ...") and keeps its own
 * presentation, so it is left out on purpose.
 *
 * Untis reports a class that has been taken out of the timetable as "Freisetzung" (`Release`)
 * rather than a cancellation, so checking for `Cancellation` alone leaves such lessons looking
 * like regular ones (Bug 11673).
 */
export type NotOccurringMotive = 'Cancellation' | 'NoLesson' | 'Release';

export const NOT_OCCURRING_MOTIVES: ReadonlySet<string> = new Set<NotOccurringMotive>([
  'Cancellation',
  'NoLesson',
  'Release',
]);

/**
 * Returns true if any of the given motives states that the lesson does not take place.
 *
 * Takes plain strings so both motive representations fit: the generated `SubstitutionMotive`
 * enum of the V2 API and the string literal union of the substitution widget. Both carry the
 * same values.
 *
 * A single motive may hold several flags: endpoints that return the motive as one value send a
 * combination as one comma separated string, e.g. `"Release, RoomExchange"`.
 */
export const isNotOccurringMotive = (
  motives: string | readonly string[] | null | undefined,
): boolean => {
  if (!motives) {
    return false;
  }

  const flags = typeof motives === 'string' ? motives.split(',') : motives;
  return flags.some((motive) => NOT_OCCURRING_MOTIVES.has(motive.trim()));
};
